import { useEffect, useRef } from 'react';
import { Button as DaisyButton, Modal } from 'react-daisyui';
import { Button } from '@leadsales/design-system/react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Trans, useTranslation } from 'react-i18next';
import classNames from 'classnames';
import RadioCard from 'src/components/RadioCard/RadioCard.component';
import RadioCardGroup from 'src/components/RadioCard/RadioCardGroup.component';
import MoneyInput from 'src/components/MoneyInput/MoneyInput.component';
import type { tTopUpKind, tTopUpModalProps } from './TopUpModal.types';
import './TopUpModal.styles.scss';

/**
 * El `name` del grupo de radios del tipo de recarga. Constante porque desde
 * RET-2348 lo leen dos lugares de este archivo —el grupo que lo declara y el
 * regreso del foco que lo busca— y un literal repetido se desincroniza solo.
 */
const KIND_GROUP_NAME = 'wallet-top-up-kind';

/** The type-blocked notice; the blocked option points at it (RC-22). */
const TYPE_BLOCKED_NOTICE_ID = 'wallet-top-up-type-blocked';

/**
 * The hints under the threshold form's fields; each field points at its hint
 * with `aria-describedby` (RET-2425), so a screen reader reads the rule when
 * the field takes focus.
 */
const THRESHOLD_HINT_ID = 'wallet-top-up-threshold-hint';
const AMOUNT_HINT_ID = 'wallet-top-up-amount-hint';

/**
 * Top-up modal (RET-1690 / 1691 / 1692, prototype screen 5).
 *
 * Presentational: every value and every handler arrives by prop, so the whole
 * screen can be reviewed against the prototype while the checkout endpoint
 * (RET-1757) does not exist. The container owns the state.
 *
 * Money rules that shape this component:
 * - Presets are backend data, never a constant here. For recurring top-ups
 *   they scale with the plan cadence, so a hardcoded ladder would be wrong for
 *   every non-monthly customer.
 * - Preset labels are printed as they arrive. The only figure this component
 *   ever composes is the customer's own typed input, prefixed with the
 *   currency symbol — it never formats an amount we calculated.
 * - The currency is the subscription's and is not selectable (Stripe
 *   constraint, D-10). It is displayed, never offered as a choice.
 */
export default function TopUpModal(props: tTopUpModalProps) {
  const { t } = useTranslation('wallet');
  const {
    isOpen,
    kind,
    presets,
    selectedPresetMicros,
    customAmount,
    totalLabel,
    // Vacío = todavía no hay un monto que se pueda cobrar: el botón dice
    // «Recargar» a secas (RET-2348, ver el docblock de la prop).
    chargeAmountLabel = '',
    recurring = null,
    activeRecurring = null,
    // Omitida = «no sé», y aquí eso significa «no la ofrezcas». Era `true`, y
    // ese default convertía la ausencia en una afirmación: cualquier consumidor
    // que no pasara la prop —o que pasara el `undefined` de un contexto a medio
    // resolver— abría la pestaña, con lo que el gate del contenedor quedaba
    // decorativo. Vale también como el lado seguro de un despiste: la recarga
    // única siempre está, así que un false de más nunca deja al cliente sin
    // poder recargar.
    canChooseRecurring = false,
    paymentSection = null,
    isSubmitting = false,
    errorMessage = null,
    // Fail cerrado ante un consumidor que omita la prop — ver su docblock en
    // TopUpModal.types.ts (RET-1933).
    errorBlocksContinue = true,
    customAmountError = null,
    // Las dos omitidas = «no sé», y sin saber no se afirma nada: ni que se
    // guardó una tarjeta ni que el cliente no puede enviar (RET-2348).
    cardSaved = false,
    walletCannotSend = false,
    scheduledCancellation = null,
    onKeepPlan,
    // RET-2425. Every one omitted = today's modal: no threshold option, no new
    // layout, no new notice.
    thresholdDesign = false,
    canChooseThreshold = false,
    thresholdOptionLoading = false,
    threshold = null,
    activeThreshold = null,
    typeBlocked = null,
    pausedThreshold = null,
    onClose,
    onKindChange,
    onSelectPreset,
    onCustomAmountChange,
    onContinue,
  } = props;

  const isRecurring = kind === 'recurring';
  /*
   * RET-2425: the third kind. Every branch below that used to read
   * `!isRecurring` as "one-off" now names its kind, so the threshold top-up
   * never falls into the one-off path (its button, its footer, its hint).
   *
   * `threshold` can be null while `kind === 'threshold'` (the container has
   * not built the form): nothing of the form renders and Continue stays off.
   */
  const isThreshold = kind === 'threshold';
  // Editing the threshold top-up in force: «Guardar cambios», off when nothing changed.
  const isEditingThreshold = isThreshold && Boolean(activeThreshold);
  const isUnchangedEdit = isEditingThreshold && Boolean(threshold?.unchanged);
  const canContinue = props.canContinue ?? false;
  /**
   * PD-4's retention moment: the customer picked the automatic top-up while
   * the cancellation is scheduled. `scheduledCancellation` is the ONLY
   * signal this component reads for the whole PD-4 tree — it never
   * re-derives it from a status, so it can't diverge from the container's
   * `shouldReactivateInPlace` call.
   *
   * RET-2425 (KTD9): the threshold top-up is a retention moment only when
   * there is NO active one. PD-4 says a new one is not offered, but one already
   * active keeps charging until the effective date and its amounts can still
   * be changed; a retention block there would be a dead end.
   */
  const isRetentionMoment =
    Boolean(scheduledCancellation) &&
    (isRecurring || (isThreshold && !activeThreshold));
  /**
   * RET-2259: la automática vigente es un paquete de créditos convertido. No
   * se reemplaza desde aquí —el PUT de reemplazo no lo conoce—, así que en vez
   * del aviso de reemplazo se avisa que primero hay que apagarlo desde el
   * switch, y Continuar queda apagado con esa razón a la vista.
   */
  const isCreditPackageActive =
    isRecurring &&
    activeRecurring?.source === 'credit_package' &&
    !isRetentionMoment;
  /*
   * El adorno del campo donde el cliente teclea su monto.
   *
   * Es "$" en las DOS monedas, igual que los presets de arriba: desde el
   * 18-ago-2026 el formateador del wallet usa `narrowSymbol` y distingue la
   * moneda por el código que va al final del importe, no por el símbolo. Dejarlo
   * en "US$" haría que el campo contradijera a los botones que tiene encima.
   *
   * La verdad de ese formato vive en Wallet.money.ts (`walletAmountAffixes`), y
   * el total que se arma ahí es el que lleva el código; aquí sólo se pinta el
   * prefijo, porque este componente se dibuja sin locale ni backend a propósito.
   */
  const currencySymbol = '$';

  /*
   * RET-2425 (RC-22): the automatic type that cannot be chosen now. Its radio
   * stays focusable (`aria-disabled`, not `disabled`) and points at the
   * notice under the options; a click or an arrow key onto it changes nothing.
   * If the modal is already on that kind, Continue stays off: the notice says
   * why.
   */
  const isKindBlocked = typeBlocked !== null && typeBlocked.kind === kind;

  /*
   * RC-20 in the threshold form: saving charges right away. One predicate for
   * the footer line AND the button, so they can never disagree.
   *
   * - `belowBalanceLabel` is a front-end prediction (KTD8); with a charge in
   *   progress (`inProgressLabel`) saving does not charge, so nothing promises
   *   a charge (RC-17).
   * - Without a valid limit, with the amount rule broken, or without an amount
   *   the button would charge, there is no charge to announce.
   */
  const thresholdChargesOnSave =
    isThreshold &&
    threshold !== null &&
    Boolean(threshold.belowBalanceLabel) &&
    !threshold.inProgressLabel &&
    Boolean(threshold.thresholdLabel) &&
    !threshold.error &&
    !threshold.amountInvalid &&
    Boolean(chargeAmountLabel);

  /*
   * What keeps Continue off in the threshold form (R4): no form, no valid
   * limit, a limit error, a preset that does not exceed the limit, or an edit
   * that changes nothing.
   */
  const thresholdBlocksContinue =
    isThreshold &&
    (threshold === null ||
      !threshold.thresholdLabel ||
      Boolean(threshold.error) ||
      threshold.amountInvalid ||
      isUnchangedEdit);

  const bodyRef = useRef<HTMLDivElement>(null);
  /*
   * RET-2425: with the fixed footer the body scrolls, so an error at the end
   * of the body can sit below the fold after the customer presses the button.
   * It is brought into view when it appears. Only in the new design: today's
   * modal has no scrolling body. `?.()` because jsdom has no scrollIntoView.
   */
  const errorRef = useRef<HTMLParagraphElement>(null);
  useEffect(() => {
    if (thresholdDesign && errorMessage) {
      errorRef.current?.scrollIntoView?.({ block: 'nearest' });
    }
  }, [thresholdDesign, errorMessage]);

  /**
   * «Hacer una recarga única», la salida del aviso de la pestaña automática
   * (RET-2348).
   *
   * No tiene camino propio: es el MISMO cambio de pestaña que la tarjeta de
   * «Recarga única», así que las dos puertas no pueden dejar el modal en
   * estados distintos. Lo que restaura y lo que suelta lo decide el contenedor.
   *
   * LO ÚNICO QUE AGREGA ES EL FOCO. El botón se desmonta con su propio clic
   * (el aviso sólo existe en la pestaña automática), y un control enfocado que
   * desaparece suelta el foco en el `<body>`: quien navega con teclado o con
   * lector de pantalla quedaría fuera del modal, detrás de él. Se lleva al
   * radio de «Recarga única», que es el control que dice dónde quedó el
   * cliente. Ese radio YA está en el documento —las dos tarjetas se pintan
   * siempre—, así que se enfoca aquí mismo, sin esperar al render.
   *
   * Se busca por `name` y `value` porque `RadioCardGroup` no expone un ref a
   * sus radios, y dentro de `bodyRef` para no tocar otro grupo de la página.
   */
  function switchToOneOff() {
    onKindChange('one-off');
    bodyRef.current
      ?.querySelector<HTMLInputElement>(
        `input[name="${KIND_GROUP_NAME}"][value="one-off"]`,
      )
      ?.focus();
  }

  /*
   * RET-2425: the threshold option goes second, between the one-off and the
   * recurring one (RC-01), and only in the new design. While its availability
   * loads, a placeholder of the same size holds the spot (`placeholder`), so
   * the options do not move when it resolves (KTD4).
   */
  const showsThresholdOption = thresholdDesign && canChooseThreshold;
  const showsThresholdPlaceholder =
    thresholdDesign && !canChooseThreshold && thresholdOptionLoading;

  const kindOptions: {
    value: tTopUpKind;
    title: string;
    hint: string;
    placeholder?: boolean;
  }[] = [
    {
      value: 'one-off',
      title: t('topUp.kind.oneOff.title'),
      hint: t('topUp.kind.oneOff.hint'),
    },
    ...(showsThresholdOption || showsThresholdPlaceholder
      ? [
          {
            value: 'threshold' as tTopUpKind,
            title: t('topUp.kind.threshold.title'),
            hint: t('topUp.kind.threshold.hint'),
            placeholder: showsThresholdPlaceholder,
          },
        ]
      : []),
    // La automática solo se ofrece si el backend manda su oferta: sin ella no
    // hay cadencia, ni fecha, ni escalera contra la que validar el alta.
    ...(canChooseRecurring
      ? [
          {
            value: 'recurring' as tTopUpKind,
            title: t('topUp.kind.recurring.title'),
            // The new design's subtitle lives in its own key, so today's
            // modal keeps today's text (KTD2).
            hint: thresholdDesign
              ? t('topUp.kind.recurring.hintThreshold')
              : t('topUp.kind.recurring.hint'),
          },
        ]
      : []),
  ];

  /*
   * The notices that say what happens with the customer's choice. Today they
   * sit between the amounts and «Método de pago»; the new design moves them
   * after it, next to the button (KTD2). They are built once and placed by
   * `thresholdDesign`, so the two layouts can never paint different notices.
   */
  const startNotice =
    isRecurring && recurring && !isRetentionMoment ? (
      <>
        {/*
        Con una recarga automática YA configurada esta frase sobra, y no
        por repetirse: el aviso de reemplazo de abajo dice la MISMA fecha
        de primer cobro y además lo único que en ese caso hace falta
        —que sustituye a la vigente y que no hay cobro extra este
        ciclo—. Dos recuadros seguidos prometiendo un primer cobro se
        leen como dos cobros, que es justo el miedo de quien ya tiene una
        recarga corriendo. Sin recarga activa la frase se queda: ahí es
        la única que explica cuándo empieza.
      */}
        {/*
        RET-2348: sin saldo, el aviso ocupa ESTE lugar y la frase de
        «El primer cobro…» no se pinta. RC-04 dice que la automática
        siempre inicia el próximo ciclo; lo que faltaba era decir su
        consecuencia a quien hoy no puede enviar. Las dos dicen la misma fecha,
        y por la misma razón que el reemplazo: dos recuadros seguidos con
        esa fecha se leen como dos cobros. La que se queda es la que le
        dice a quien no puede enviar lo único que le falta saber, que hoy
        su saldo no cambia, y le da la salida a un clic. La cadencia no
        se pierde: sigue en «Frecuencia» y en el resumen del pie.

        Va también con una recarga ya configurada, encima del aviso de
        reemplazo: quien se quedó sin saldo a mitad de ciclo y viene a
        subir el monto de su automática cae en la misma trampa.

        NO va con un paquete convertido vigente: ahí no hay alta posible
        (RET-2259), así que «esta recarga se cobra el…» describiría un
        cobro que no va a ocurrir. El momento de retención queda fuera
        por lo mismo, y de eso ya se encarga la condición de este bloque.

        Y la variante SIN FECHA, igual que el resto de esta pestaña: sin
        `startDateLabel` la frase de siempre diría «se cobra el .».
      */}
        {walletCannotSend && !isCreditPackageActive ? (
          <div
            className="TopUpModal__NoBalance"
            data-testid="wallet-top-up-no-balance-notice"
          >
            <ExclamationTriangleIcon
              className="size-5 shrink-0 mt-0.5"
              aria-hidden="true"
            />
            <div className="TopUpModal__NoBalanceBody">
              {/* `status`, no `alert`: informa y no bloquea, igual que la
                barra sin saldo del compositor. El botón queda FUERA de
                la región viva para que el lector de pantalla anuncie la
                frase y no el control. */}
              <p role="status">
                {recurring.startDateLabel
                  ? t('topUp.recurringNoBalance.notice', {
                      date: recurring.startDateLabel,
                    })
                  : t('topUp.recurringNoBalance.noticeNoDate')}
              </p>
              {/* Mismo botón de texto que «Agregar otro método de pago»,
                su vecino en este modal (TopUpPaymentSection): `link`,
                `sm`, `w-fit px-0`. Lo único distinto es el color: aquel
                fija `text-neutral-content`, que vale casi negro en los
                DOS temas; éste hereda el del aviso, que sale de un token
                con valor oscuro (ver `__NoBalance`). */}
              <DaisyButton
                variant="link"
                size="sm"
                className="w-fit px-0 text-inherit"
                onClick={switchToOneOff}
                data-testid="wallet-top-up-switch-to-one-off"
              >
                {t('topUp.recurringNoBalance.oneOffInstead')}
              </DaisyButton>
            </div>
          </div>
        ) : activeRecurring || activeThreshold ? null : (
          <p
            className="TopUpModal__Explain"
            data-testid="wallet-top-up-start-explain"
          >
            {/* RC-04: the adverbial form, not the adjective lowercased —
              that trick only reads as an adverb in English ("monthly" is
              both); es/br need a different word ("mensualmente" /
              "mensalmente"), and `cadenceLabel.toLowerCase()` was
              producing "se repite mensual" / "se repete mensal". */}
            {/*
            Y la variante SIN FECHA, por la misma razón por la que el pie
            tiene la suya: `firstCharge` está ausente cuando el backend
            no tiene fecha que prometer, y con la frase de siempre el
            cliente leía "El primer cobro se realiza el , al iniciar tu
            próximo ciclo" — preposición colgando y coma huérfana, en los
            tres locales. El pie ya resolvía su borde y el cuerpo, tres
            líneas arriba, seguía roto.

            Lo que se pierde es solo el DÍA: que arranca el próximo ciclo
            y con qué cadencia se repite sigue dicho, porque eso sí se
            sabe.
          */}
            {/* Las dos variantes interpolan la cadencia, asi que sin
              ella no hay frase que componer: la fila de la fecha,
              arriba, ya dice lo unico que se sabe. */}
            {!recurring.cadenceAdverbLabel
              ? null
              : recurring.startDateLabel
                ? t('topUp.start.explain', {
                    date: recurring.startDateLabel,
                    cadence: recurring.cadenceAdverbLabel,
                  })
                : t('topUp.start.explainNoDate', {
                    cadence: recurring.cadenceAdverbLabel,
                  })}
          </p>
        )}
      </>
    ) : null;

  const replaceNotices = (
    <>
      {/* RET-1692: only one recurring top-up can be active. Shown before
        committing, so replacing is never a surprise. */}
      {isCreditPackageActive && activeRecurring ? (
        <p
          className="TopUpModal__Replace"
          data-testid="wallet-top-up-credit-package-notice"
        >
          <Trans
            t={t}
            i18nKey="topUp.creditPackage.notice"
            values={{ amount: activeRecurring.amountLabel }}
            components={[<strong key="0" className="font-semibold" />]}
          />
        </p>
      ) : null}

      {isRecurring &&
      activeRecurring &&
      !isRetentionMoment &&
      !isCreditPackageActive ? (
        <p
          className="TopUpModal__Replace"
          data-testid="wallet-top-up-replace-notice"
        >
          {/*
          El monto de la recarga vigente va resaltado: es el dato que el
          cliente busca para decidir si de verdad quiere reemplazarla, y
          en una frase de tres renglones se pierde. Va por `Trans` con la
          convención de índices del repo (`<0>…</0>`) y no partiendo la
          copia en dos llaves: el orden de las palabras alrededor del monto
          cambia entre los tres locales, y una frase en trozos se traduce
          mal por construcción.
        */}
          {/*
          Y la variante SIN FECHA, por lo mismo que la del diálogo que
          confirma: `activeRecurring` sólo exige el MONTO para pintarse
          —sin él no hay frase—, pero la fecha puede faltar, porque el back
          la degrada a vacío cuando no resuelve la suscripción del plan.
          Con la frase de siempre el cliente leía "desde tu próximo ciclo
          (), sin cobro adicional": un paréntesis vacío en mitad de una
          promesa de cobro.
        */}
          <Trans
            t={t}
            i18nKey={
              activeRecurring.startDateLabel
                ? 'topUp.replace.notice'
                : 'topUp.replace.noticeNoDate'
            }
            values={{
              amount: activeRecurring.amountLabel,
              date: activeRecurring.startDateLabel,
            }}
            components={[<strong key="0" className="font-semibold" />]}
          />
        </p>
      ) : null}
    </>
  );

  /*
   * RET-2425: the notices between the two automatic types and of the threshold
   * form itself (RC-17, RC-22). New design only.
   */
  const thresholdNotices = (
    <>
      {/* The recurring top-up replaces the threshold one AT ONCE: the notice
          says the gap — no automatic top-up until its first charge. */}
      {isRecurring &&
      activeThreshold &&
      !activeRecurring &&
      !isRetentionMoment ? (
        <p
          className="TopUpModal__Replace"
          data-testid="wallet-top-up-replace-threshold"
        >
          <Trans
            t={t}
            i18nKey={
              recurring?.startDateLabel
                ? 'topUp.replace.thresholdByRecurring'
                : 'topUp.replace.thresholdByRecurringNoDate'
            }
            values={{
              amount: activeThreshold.amountLabel,
              date: recurring?.startDateLabel,
            }}
            components={[<strong key="0" className="font-semibold" />]}
          />
        </p>
      ) : null}

      {/* The «your balance is already below» line is NOT here: it lives in the
          footer, which is always in view next to the button that charges. */}
      {isThreshold && threshold && !isRetentionMoment ? (
        <>
          {activeRecurring ? (
            <p
              className="TopUpModal__Replace"
              data-testid="wallet-top-up-threshold-replaces-recurring"
            >
              <Trans
                t={t}
                i18nKey="topUp.threshold.replaceRecurring"
                values={{ amount: activeRecurring.amountLabel }}
                components={[<strong key="0" className="font-semibold" />]}
              />
            </p>
          ) : null}
          {/* Never two charges in flight (RC-17): with one in progress, saving
              does not charge. It REPLACES the self-replace sentence: two
              notices using «recarga» for the configuration and for the charge
              read as a contradiction. */}
          {threshold.inProgressLabel ? (
            <p
              className="TopUpModal__Replace"
              data-testid="wallet-top-up-threshold-in-progress"
            >
              {t('topUp.threshold.inProgress', {
                amount: threshold.inProgressLabel,
              })}
            </p>
          ) : null}
          {!activeRecurring &&
          !threshold.inProgressLabel &&
          activeThreshold &&
          !isUnchangedEdit ? (
            <p
              className="TopUpModal__Replace"
              data-testid="wallet-top-up-threshold-replaces-self"
            >
              {t('topUp.threshold.replaceSelfIntro')}
            </p>
          ) : null}
        </>
      ) : null}
    </>
  );

  /*
   * The type-blocked notice (RC-22), copy approved by product on 19-oct.
   */
  const typeBlockedMessage =
    typeBlocked === null
      ? null
      : typeBlocked.reason === 'past_due'
        ? t('topUp.typeBlocked.pastDue')
        : typeBlocked.dateLabel
          ? t('topUp.typeBlocked.creditPackageScheduled', {
              date: typeBlocked.dateLabel,
            })
          : t('topUp.typeBlocked.creditPackageScheduledNoDate');

  /*
   * The presets, shared by both layouts. `--invalid` only ever applies to the
   * threshold form (RC-18): a preset that does not exceed the limit can be
   * picked, and picked it shows red while the rule below turns into the error.
   */
  const presetButtons = presets.map((preset) => {
    const isSelected = selectedPresetMicros === preset.amountMicros;

    return (
      <button
        key={preset.amountMicros}
        type="button"
        aria-pressed={isSelected}
        onClick={() => onSelectPreset(preset.amountMicros)}
        // En el momento de retención no hay nada que elegir: la
        // recurrente no se puede dar de alta, así que una escalera de
        // montos viva invita a configurar algo que no va a ocurrir.
        // Con un paquete convertido vigente pasa lo mismo (RET-2259):
        // no hay alta posible hasta apagarlo.
        disabled={isRetentionMoment || isCreditPackageActive}
        className={classNames('TopUpModal__Amount', {
          'TopUpModal__Amount--on': isSelected,
          'TopUpModal__Amount--invalid':
            isSelected && isThreshold && Boolean(threshold?.amountInvalid),
        })}
        data-testid={`wallet-top-up-preset-${preset.amountMicros}`}
      >
        {preset.label}
      </button>
    );
  });

  /*
   * The free amount takes whole units: one-off (RC-10) and threshold (RC-18).
   * The recurring top-up takes presets only, so the ladder can scale with the
   * plan cadence (D-3).
   */
  const showsFreeAmount =
    kind === 'one-off' || (isThreshold && !isRetentionMoment);

  /*
   * RadioCardGroup keeps a real radio group underneath: one tab stop,
   * arrow-key navigation and proper announcements, which a row of buttons with
   * aria-checked does not give for free.
   */
  const kindGroup = (
    <RadioCardGroup
      name={KIND_GROUP_NAME}
      value={kind}
      onChange={(value) => onKindChange(value as tTopUpKind)}
      orientation="horizontal"
      equalWidth
      // Three cards do not fit side by side on a phone: below md they
      // stack, each full width (RET-2425).
      stackBelowMd={thresholdDesign}
      ariaLabelledBy="wallet-top-up-kind-label"
    >
      {kindOptions.map((option) =>
        option.placeholder ? (
          /* The same box and the same (invisible) text as the real card,
           so it takes exactly its size. Not a radio: there is nothing to
           choose yet. */
          <RadioCard
            key={option.value}
            value={option.value}
            placeholder
            data-testid="wallet-top-up-kind-threshold-placeholder"
          >
            <span className="text-sm font-bold">{option.title}</span>
            <span className="text-xs">{option.hint}</span>
          </RadioCard>
        ) : (
          <RadioCard
            key={option.value}
            value={option.value}
            ariaDisabled={typeBlocked?.kind === option.value || undefined}
            ariaDescribedBy={
              typeBlocked?.kind === option.value
                ? TYPE_BLOCKED_NOTICE_ID
                : undefined
            }
          >
            <span className="text-sm font-bold">{option.title}</span>
            <span className="text-xs text-base-content/60">{option.hint}</span>
          </RadioCard>
        ),
      )}
    </RadioCardGroup>
  );

  /*
   * RC-22: why the other automatic type cannot be chosen. Right under the
   * options, so the blocked card and its reason read together.
   */
  const typeBlockedNotice = typeBlockedMessage ? (
    <p
      className="TopUpModal__TypeBlocked"
      id={TYPE_BLOCKED_NOTICE_ID}
      role="status"
      data-testid="wallet-top-up-type-blocked"
    >
      {typeBlockedMessage}
    </p>
  ) : null;

  return (
    <Modal.Legacy
      open={isOpen}
      onClickBackdrop={onClose}
      /*
       * RET-2425 (KTD2): 48rem (768 px) instead of 38.5rem (616 px) — with
       * three types at 616 px the card titles broke into three lines. The same
       * width for every type, so the modal does not resize on a pick. The
       * modifier also fixes the footer and scrolls only the body. Below that
       * it is the viewport minus a 2rem gutter (`--ls-spacing-8`, 32 px), so
       * the footer button is not clipped on a phone; `max-w-none` lifts
       * daisyUI's own cap on the box.
       */
      className={
        thresholdDesign
          ? 'TopUpModal TopUpModal--threshold rounded-lg p-0 w-[min(48rem,calc(100vw-var(--ls-spacing-8)))] max-w-none'
          : 'TopUpModal rounded-lg p-0 min-w-[38.5rem]'
      }
    >
      <header className="TopUpModal__Head">
        <div className="flex flex-col">
          <h1 className="text-lg font-semibold">{t('topUp.title')}</h1>
          <p className="text-sm text-neutral-500">{t('topUp.subtitle')}</p>
        </div>
        <button type="button" onClick={onClose} aria-label={t('topUp.close')}>
          <XMarkIcon className="size-6 cursor-pointer" />
        </button>
      </header>

      <div className="TopUpModal__Body" ref={bodyRef}>
        {/* PD-4 (leadbilling/docs/business-rules/wallet/pago-vencido.md):
            informs, does not block — the recharge below still goes through
            while the cancellation is scheduled. Hidden while the retention
            block is up (`isRetentionMoment`): that block already says the
            same fact with an offer attached, and showing both would repeat
            the warning right above its own explanation. */}
        {scheduledCancellation && !isRetentionMoment ? (
          <p
            className="TopUpModal__CancellationNotice"
            data-testid="wallet-top-up-cancellation-notice"
          >
            <ExclamationTriangleIcon
              className="size-5 shrink-0 mt-0.5"
              aria-hidden="true"
            />
            <span>
              {scheduledCancellation.dateLabel
                ? t('topUp.scheduledCancellation.notice', {
                    date: scheduledCancellation.dateLabel,
                  })
                : t('topUp.scheduledCancellation.noticeNoDate')}
            </span>
          </p>
        ) : null}

        <p className="TopUpModal__SectionLabel" id="wallet-top-up-kind-label">
          {t('topUp.kind.label')}
        </p>
        {thresholdDesign ? (
          /* The options and their notice are one block that owns the distance
             between them (8 px), instead of a negative margin on the notice. */
          <div className="flex flex-col gap-2">
            {kindGroup}
            {typeBlockedNotice}
          </div>
        ) : (
          <>
            {kindGroup}
            {typeBlockedNotice}
          </>
        )}

        {/* PD-4's retention moment: picking the automatic top-up while the
            cancellation is scheduled. No próxima renovación que la cobre, así
            que se ofrece des-agendar en vez de la ficha de cadencia/inicio
            que el resto de esta pestaña muestra — esa ficha promete un cobro
            que no va a ocurrir. */}
        {isRetentionMoment ? (
          <div
            className="TopUpModal__Retention"
            data-testid="wallet-top-up-retention"
          >
            <p className="TopUpModal__RetentionTitle">
              {t('topUp.scheduledCancellation.retention.title')}
            </p>
            <p className="TopUpModal__RetentionBody">
              {isThreshold
                ? scheduledCancellation?.dateLabel
                  ? t('topUp.scheduledCancellation.retention.bodyThreshold', {
                      date: scheduledCancellation.dateLabel,
                    })
                  : t(
                      'topUp.scheduledCancellation.retention.bodyThresholdNoDate',
                    )
                : t('topUp.scheduledCancellation.retention.body')}
            </p>
            <Button color="primary" size="sm" onClick={onKeepPlan}>
              {t('topUp.scheduledCancellation.retention.keepPlan')}
            </Button>
          </div>
        ) : null}

        {isRecurring && recurring && !isRetentionMoment ? (
          <>
            <p className="TopUpModal__SectionLabel">
              {t('topUp.cadence.label')}
            </p>
            {/* Inherited from the plan and not editable — informative only. */}
            {/* Sin periodicidad no hay palabra de cadencia que mostrar; la
                FECHA de abajo sí sobrevive (RC-04, informativa). */}
            {recurring.cadenceLabel ? (
              <p className="TopUpModal__Info">
                {recurring.cadenceLabel}
                <span>{t('topUp.cadence.inherited')}</span>
              </p>
            ) : null}
          </>
        ) : null}

        {/* RET-2425: the limit goes BEFORE the amount — it is the condition,
            and the amount is validated against it (always greater). Whole
            units, like the free amount (RC-18). */}
        {isThreshold && threshold && !isRetentionMoment ? (
          <>
            <p
              className="TopUpModal__SectionLabel"
              id="wallet-top-up-threshold-label"
            >
              {t('topUp.threshold.label')}
            </p>
            <MoneyInput
              ariaLabel={t('topUp.threshold.label')}
              value={threshold.value}
              onChange={threshold.onChange}
              currencySymbol={currencySymbol}
              maxDecimals={0}
              placeholder="0"
              error={threshold.error ?? undefined}
              // The hint renders only without an error: point at it only then.
              ariaDescribedBy={threshold.error ? undefined : THRESHOLD_HINT_ID}
              data-testid="wallet-top-up-threshold"
            />
            {threshold.error ? null : (
              <p className="TopUpModal__WholeOnlyHint" id={THRESHOLD_HINT_ID}>
                {t('topUp.threshold.hint', { min: threshold.minLabel })}
              </p>
            )}
          </>
        ) : null}

        <p className="TopUpModal__SectionLabel">{t('topUp.amount.label')}</p>
        {thresholdDesign ? (
          <>
            {/* The presets and the free amount are ONE control: no «Otro
                monto» label, which read as another step; the field says it in
                its placeholder and sits right under the presets. */}
            <div className="TopUpModal__AmountGroup">
              <div className="TopUpModal__Amounts">{presetButtons}</div>
              {showsFreeAmount ? (
                <MoneyInput
                  ariaLabel={t('topUp.amount.custom')}
                  value={customAmount}
                  onChange={onCustomAmountChange}
                  currencySymbol={currencySymbol}
                  // RC-10: whole units only; see the same prop below.
                  maxDecimals={0}
                  placeholder={t('topUp.amount.custom')}
                  error={customAmountError ?? undefined}
                  // The help line below renders only without an error.
                  ariaDescribedBy={
                    customAmountError ? undefined : AMOUNT_HINT_ID
                  }
                  data-testid="wallet-top-up-custom-amount"
                />
              ) : null}
            </div>
            {/* ONE help line under the amount group (R5). With an error, the
                error takes its place (MoneyInput paints it) and the help does
                not show. With presets that do not exceed the limit, the help
                says why and what to pick instead of «Usa montos enteros». */}
            {showsFreeAmount && !customAmountError ? (
              isThreshold && threshold?.amountHint ? (
                <p
                  className={classNames('TopUpModal__WholeOnlyHint', {
                    'TopUpModal__WholeOnlyHint--error': threshold.amountInvalid,
                  })}
                  id={AMOUNT_HINT_ID}
                  role={threshold.amountInvalid ? 'alert' : undefined}
                  data-testid="wallet-top-up-threshold-amount-hint"
                >
                  {threshold.amountHint}
                </p>
              ) : (
                <p className="TopUpModal__WholeOnlyHint" id={AMOUNT_HINT_ID}>
                  {t('topUp.amount.wholeOnly')}
                </p>
              )
            ) : null}
          </>
        ) : (
          <>
            <div className="TopUpModal__Amounts">{presetButtons}</div>

            {/* Free amount is one-off only: a recurring charge takes presets so the
                ladder can scale with the plan cadence (D-3). */}
            {showsFreeAmount ? (
              <>
                <MoneyInput
                  label={t('topUp.amount.custom')}
                  value={customAmount}
                  onChange={onCustomAmountChange}
                  currencySymbol={currencySymbol}
                  // RC-10: the free amount is WHOLE units only, no cents. This
                  // alone does not finish the job — MoneyInput settles a
                  // dangling separator on blur only when `maxDecimals === 0`
                  // (see settleMoneyInput in MoneyInput.functions.ts for why
                  // that has to happen on blur and not mid-keystroke).
                  maxDecimals={0}
                  placeholder="0"
                  error={customAmountError ?? undefined}
                  data-testid="wallet-top-up-custom-amount"
                />
                {/* Prototype screen 5. Said up front rather than left for the
                    customer to discover by typing a decimal and watching it get
                    cut off. */}
                <p className="TopUpModal__WholeOnlyHint">
                  {t('topUp.amount.wholeOnly')}
                </p>
              </>
            ) : null}
          </>
        )}

        {isRecurring && recurring && !isRetentionMoment ? (
          <>
            <p className="TopUpModal__SectionLabel">{t('topUp.start.label')}</p>
            {/*
              La ficha de la fecha solo existe si hay fecha. Un rótulo
              ("Próximo ciclo") junto a un valor vacío no informa: parece un
              dato que no cargó.
            */}
            {recurring.startDateLabel ? (
              <p className="TopUpModal__Info">
                {t('topUp.start.nextCycle')}
                <span>{recurring.startDateLabel}</span>
              </p>
            ) : null}
            {thresholdDesign ? null : startNotice}
          </>
        ) : null}

        {thresholdDesign ? null : replaceNotices}

        <p className="TopUpModal__SectionLabel">{t('topUp.payment.label')}</p>
        {/*
          El bloque de pago llega ARMADO desde el contenedor. Este componente se
          dibuja sin backend a propósito, y el formulario de Stripe necesita el
          contexto del manejador de suscripción: montarlo aquí ataría la revisión
          visual de todo el modal a tener ese provider en pie.
        */}
        {paymentSection}

        {/*
          RET-2348: guardar una tarjeta sólo la guarda. El aviso flotante dice
          «Tarjeta guardada» y se va a los 5 s; esta línea se queda, justo
          debajo de la tarjeta de la que habla y en el lugar donde un momento
          antes estaba el rechazo, diciendo lo que falta: la recarga sigue sin
          cobrarse.

          `status`, no `alert`: es información, y el rechazo que reemplaza sí
          era el error. Cuándo aparece y cuándo se va lo decide el contenedor.
        */}
        {cardSaved ? (
          <p
            className="TopUpModal__Notice"
            role="status"
            data-testid="wallet-top-up-card-saved"
          >
            {t('topUp.cardSaved.notice')}
          </p>
        ) : null}

        {errorMessage ? (
          <p className="TopUpModal__Error" role="alert" ref={errorRef}>
            {errorMessage}
          </p>
        ) : null}

        {/* RET-2425 (KTD2): in the new design the notices go AFTER «Método de
            pago» — first what the customer picks (type, amounts, card), then
            what happens with it, next to the button. The decline and «Tarjeta
            guardada» stay right under the card: they share that line. */}
        {thresholdDesign ? (
          <>
            {startNotice}
            {replaceNotices}
            {thresholdNotices}
          </>
        ) : null}
      </div>

      <footer className="TopUpModal__Foot">
        {/*
          El resumen desaparece en el momento de retención, y no es cosmético:
          decía "$50 USD · se cobra cada ciclo, no vence" a dos centímetros del
          bloque que explica que ESE cobro no va a ocurrir. Dos frases que se
          contradicen en la misma pantalla, y la de abajo es la que parece
          resumen de lo que estás por aceptar.

          Se oculta el bloque entero en vez de reescribir el texto: no hay
          monto que resumir cuando no hay nada que continuar.
        */}
        {isRetentionMoment ? (
          <div />
        ) : (
          <div>
            {/* RET-2425: both automatic types say «Nueva configuración de
                recarga automática», also when changing amounts — what shows is
                what gets saved. «Recargar» stays for the one-off, the one this
                click charges. */}
            <p className="TopUpModal__FootLabel">
              {isThreshold || (thresholdDesign && isRecurring)
                ? t('topUp.footer.autoLabel')
                : t('topUp.footer.label')}
            </p>
            <p
              className="TopUpModal__FootAmount"
              data-testid="wallet-top-up-total"
            >
              {/* A preset that does not exceed the limit cannot be saved, so
                  the footer does not summarize it (RC-18). */}
              {isThreshold && threshold?.amountInvalid ? null : totalLabel}
            </p>
            <p
              className="TopUpModal__FootKind"
              data-testid="wallet-top-up-foot-kind"
            >
              {/*
                RC-04: the summary names the CADENCE (adverbial form) and the
                first-charge DATE — the same `startDateLabel` the body already
                paints in "Fecha de inicio", one source, not two formatters.

                `recurring` can be null even while `isRecurring` is true (the
                backend has not sent recurring info yet): that keeps the OLD
                type hint as a defensive fallback rather than rendering a
                sentence built from nothing.

                `startDateLabel` is '' when the backend has no date to
                promise (RC-04's own edge case, and the class of malformed
                copy this modal already shipped twice — RC-10's "$12. USD",
                PD-4's "1 de enero de 1970"): the NoDate variant is used
                instead of composing a dangling "desde el ".
              */}
              {/* El resumen exige CADENCIA: prometer un cobro recurrente sin
                  decir cada cuánto entiende de menos un compromiso, y eso es
                  peor que no resumir. Sin ella cae al subtítulo de siempre. */}
              {/* RET-2425: the threshold summary is one line, with the daily
                  cap (RC-19) in the same sentence. Without a valid limit it
                  falls back to the option's subtitle. */}
              {isThreshold
                ? threshold?.thresholdLabel
                  ? threshold.dailyMax
                    ? t('topUp.footer.thresholdSummaryDaily', {
                        threshold: threshold.thresholdLabel,
                        count: threshold.dailyMax,
                      })
                    : t('topUp.footer.thresholdSummary', {
                        threshold: threshold.thresholdLabel,
                      })
                  : t('topUp.kind.threshold.hint')
                : isRecurring
                  ? recurring?.cadenceAdverbLabel
                    ? recurring.startDateLabel
                      ? t('topUp.footer.recurringSummary', {
                          cadence: recurring.cadenceAdverbLabel,
                          date: recurring.startDateLabel,
                        })
                      : t('topUp.footer.recurringSummaryNoDate', {
                          cadence: recurring.cadenceAdverbLabel,
                        })
                    : thresholdDesign
                      ? t('topUp.kind.recurring.hintThreshold')
                      : t('topUp.kind.recurring.hint')
                  : t('topUp.kind.oneOff.hint')}
            </p>
            {/* RC-20 in the footer and not in the body: that saving charges has
                to be visible without scrolling, next to the button that does
                it. Never while a charge is in progress (KTD8). */}
            {thresholdChargesOnSave ? (
              <p
                className="TopUpModal__FootCharge"
                role="status"
                data-testid="wallet-top-up-threshold-below"
              >
                <ExclamationTriangleIcon
                  className="size-4 shrink-0"
                  aria-hidden="true"
                />
                {isEditingThreshold
                  ? t('topUp.threshold.alreadyBelowEdit')
                  : t('topUp.threshold.alreadyBelow')}
              </p>
            ) : null}
            {/* RET-2426 (RC-21, P43): paying a one-off with the card the paused
                threshold top-up would use ends the pause, and if the balance
                stays below the limit it charges right then. Next to the button,
                like the RC-20 line above. Only the one-off, and only with an
                amount this click could charge. */}
            {!isRecurring &&
            !isThreshold &&
            pausedThreshold &&
            chargeAmountLabel ? (
              <p
                className="TopUpModal__FootCharge"
                role="status"
                data-testid="wallet-top-up-resumes-threshold"
              >
                {pausedThreshold.chargesNow ? (
                  <ExclamationTriangleIcon
                    className="size-4 shrink-0"
                    aria-hidden="true"
                  />
                ) : null}
                {t(
                  pausedThreshold.chargesNow
                    ? 'topUp.pausedThreshold.resumesAndCharges'
                    : 'topUp.pausedThreshold.resumes',
                  {
                    amount: pausedThreshold.amountLabel,
                    threshold: pausedThreshold.thresholdLabel,
                  },
                )}
              </p>
            ) : null}
          </div>
        )}
        <Button
          color="primary"
          // `md` is 3rem tall; the design system's `lg` is 4rem, which dwarfs a
          // modal footer. (Its scale is also non-monotonic — `lg` is taller
          // than `xl` — so sizes are worth checking rather than assuming.)
          size="md"
          loading={isSubmitting}
          // Blocked without a payment method, with a validation error, or
          // with a charge error of the BLOCKING class — and the reason is
          // always on screen, never a button that looks live and does
          // nothing.
          //
          // RET-1933: `errorMessage` alone used to be enough to disable this
          // button (`Boolean(errorMessage)`), which conflated two different
          // facts under one string — what was TYPED (bad, so retrying as-is
          // is pointless) and how the CHARGE ATTEMPT went (a bank decline, a
          // dropped connection — where retrying is exactly the right move).
          // A card decline left the customer stuck rereading the same
          // rejected card with no way to try again. `errorBlocksContinue`
          // carries that class alongside the text — see its docblock and
          // `TopUpModal.chargeError.ts` — so only the errors that mean
          // "nothing will change if you press this again" keep the button
          // off.
          //
          // `customAmountError` joins the guard for the same reason it always
          // did (RC-10's minimum): an amount that fails validation must not
          // be reachable via Continue either — it is never routed through
          // `errorMessage`, so it carries no class to check.
          // `isRetentionMoment` is PD-4's own reason on screen: the
          // retention block above already explains why there is nothing to
          // continue into.
          disabled={
            isSubmitting ||
            !canContinue ||
            (Boolean(errorMessage) && errorBlocksContinue) ||
            Boolean(customAmountError) ||
            isRetentionMoment ||
            isCreditPackageActive ||
            // RET-2425: the threshold form's own reasons (R4) and RC-22's
            // blocked type, each one on screen.
            thresholdBlocksContinue ||
            isKindBlocked
          }
          onClick={onContinue}
          data-testid="wallet-top-up-continue"
        >
          {/*
            RET-2348: el botón NOMBRA lo que va a pasar. Decía «Continuar» en
            las dos pestañas, y eso dejó que un aviso de «tarjeta guardada» se
            leyera como el pago: nada en la pantalla decía que el cobro seguía
            pendiente de este clic.

            La única cobra AHORA, así que lleva el monto. La automática no
            cobra con este clic —programa un cobro para la siguiente
            renovación—, y por eso su verbo es otro y no lleva cifra: «Recargar
            $50» sobre algo que hoy no mueve dinero sería la misma confusión al
            revés.

            Sin monto válido la única dice «Recargar» a secas: nunca nombra una
            cifra que este clic no cobraría. Qué lo apaga no cambió.
          */}
          {/*
            RET-2425: the threshold top-up schedules, or saves an edit; «… y
            recargar» only when this click also charges (RC-20), never with a
            charge in progress (KTD8).
          */}
          {isThreshold
            ? isEditingThreshold
              ? thresholdChargesOnSave && !isUnchangedEdit
                ? t('topUp.footer.saveAndCharge')
                : t('topUp.footer.saveChanges')
              : thresholdChargesOnSave
                ? t('topUp.footer.configureAndCharge')
                : t('topUp.footer.configureThreshold')
            : isRecurring
              ? t('topUp.footer.scheduleRecurring')
              : chargeAmountLabel
                ? t('topUp.footer.chargeAmount', { amount: chargeAmountLabel })
                : t('topUp.footer.charge')}
        </Button>
      </footer>
    </Modal.Legacy>
  );
}
